import * as React from "react";
import { Alert, Box, Button, Card, CardContent, Container, Stack, Typography } from "@mui/material";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  getUnsubscribeInfo,
  resubscribeByToken,
  unsubscribeByToken,
} from "../../services/api/notifications";

/**
 * Landing page for the unsubscribe link in emails. Works without logging in: the token in the
 * link identifies the person. Opening the page changes nothing; the buttons do.
 * Unsubscribing only affects email, never registration or league access.
 */
export default function UnsubscribeView() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const leagueId = searchParams.get("league") || "";

  const [state, setState] = React.useState({ status: "loading", info: null, error: "" });
  const [busy, setBusy] = React.useState(false);
  const [actionError, setActionError] = React.useState("");

  const load = React.useCallback(async () => {
    if (!token) {
      setState({ status: "error", info: null, error: "This unsubscribe link is missing its token." });
      return;
    }
    try {
      const info = await getUnsubscribeInfo(token, leagueId);
      setState({ status: "ready", info, error: "" });
    } catch (err) {
      setState({
        status: "error",
        info: null,
        error: err instanceof Error ? err.message : "This unsubscribe link is not valid.",
      });
    }
  }, [token, leagueId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const run = async (action, scope) => {
    setBusy(true);
    setActionError("");
    try {
      await action(token, scope, leagueId || undefined);
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const wrapper = (children) => (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>{children}</Stack>
        </CardContent>
      </Card>
    </Container>
  );

  if (state.status === "loading") {
    return wrapper(<Typography color="text.secondary">Loading…</Typography>);
  }

  if (state.status === "error") {
    return wrapper(
      <>
        <Typography variant="h5">Email preferences</Typography>
        <Alert severity="error">{state.error}</Alert>
        <Typography variant="body2" color="text.secondary">
          If you are signed in, you can change your notification settings from your profile.
        </Typography>
        <Box>
          <Button component={RouterLink} to="/profile?tab=notifications" variant="outlined">
            Go to my notification settings
          </Button>
        </Box>
      </>
    );
  }

  const { unsubscribedAll, league } = state.info;
  const leagueMuted = league ? !(league.remindersEnabled || league.scheduleChangesEnabled || league.announcementsEnabled) : false;
  const leagueName = league?.leagueName;

  return wrapper(
    <>
      <Typography variant="h5">Email preferences</Typography>

      {unsubscribedAll ? (
        <>
          <Alert severity="success">You are unsubscribed from Puck Pickup emails.</Alert>
          <Typography variant="body2" color="text.secondary">
            You will only receive receipts for payments you make. You stay registered for your leagues and games, and you
            can still see everything in the app.
          </Typography>
          <Box>
            <Button variant="contained" disabled={busy} onClick={() => run(resubscribeByToken, "all")}>
              Undo — send me emails again
            </Button>
          </Box>
        </>
      ) : leagueMuted ? (
        <>
          <Alert severity="success">You will no longer get emails from {leagueName}.</Alert>
          <Typography variant="body2" color="text.secondary">
            You stay registered and keep your spot. You will still get payment receipts and a notice if a game you are signed
            up for is cancelled.
          </Typography>
          <Box>
            <Button variant="contained" disabled={busy} onClick={() => run(resubscribeByToken, "league")}>
              Undo — send me emails from {leagueName}
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Want to stop all Puck Pickup emails instead?
          </Typography>
          <Box>
            <Button variant="outlined" disabled={busy} onClick={() => run(unsubscribeByToken, "all")}>
              Unsubscribe from all Puck Pickup emails
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Typography variant="body1">
            {league
              ? `Choose which emails you want to stop getting.`
              : "Unsubscribe from Puck Pickup emails."}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This only changes email. You stay registered for your leagues and games, and you keep your spot.
          </Typography>
          <Stack spacing={1.5}>
            {league ? (
              <Button variant="contained" disabled={busy} onClick={() => run(unsubscribeByToken, "league")}>
                Stop emails from {leagueName}
              </Button>
            ) : null}
            <Button
              variant={league ? "outlined" : "contained"}
              disabled={busy}
              onClick={() => run(unsubscribeByToken, "all")}
            >
              Unsubscribe from all Puck Pickup emails
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            You will still get payment receipts. {league ? "Stopping a single league still sends cancellation notices for games you are signed up for." : ""}
          </Typography>
        </>
      )}

      {actionError ? <Alert severity="error">{actionError}</Alert> : null}

      <Box>
        <Button component={RouterLink} to="/profile?tab=notifications" size="small">
          Manage my notification settings
        </Button>
      </Box>
    </>
  );
}
